import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { useCommercialStructure } from 'commons/customHooks'
import {
  Alert,
  Icon,
  InputMenu,
  LoadingModal,
  Modal,
  MultiselectMenu,
  SlimButton,
  Tooltip,
  Typography,
} from 'components'
import { type Channels, criticismTypeOptions } from 'constants/enums'
import { Controller, useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  getAnalysisQuotas,
  sendAnalysisUpload,
  setSuccessUpload,
} from 'store/ExtraVolumeContext/ExtraVolumeSlice'
import { getCommercialStructure } from 'store/SharedContext/sharedSlice'
import { useDispatch, useSelector } from 'store/store'

import ModalReport from '../ModalReport'
import * as S from './styles'

export interface FilterParams {
  filtroMaterial?: string
  materialEstrategico?: boolean
  caixaGrade?: string
}

type FilterProps = {
  onSubmit: () => void
  onClear: () => void
  disableFilters: boolean
}

export const Filter = ({ onSubmit, onClear, disableFilters }: FilterProps) => {
  const dispatch = useDispatch()
  const { loadingReport, loadingUpload, successUpload, filterQuotas } = useSelector(
    (state) => state.extraVolume,
  )
  const {
    offices: officesCommercialStructure,
    regionals: regionalsCommercialStructure,
    zones: zonesCommercialStructure,
  } = useSelector((state) => state.shared)

  const {
    getMarkets,
    getOffices,
    getOfficesByMarkets,
    getRegionalsByOffices,
    getZonesByRegionals,
  } = useCommercialStructure()

  const { control, setValue } = useFormContext()
  const [openReportModal, setOpenReportModal] = useState(false)

  const xlsxFileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  const fileInputRef = useRef<HTMLInputElement>(null)
  const handleUploadClick = () => {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click()
    }
  }
  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const uploadedFile = event.target.files[0]
      if (uploadedFile.type === xlsxFileType) {
        dispatch(sendAnalysisUpload(uploadedFile))
      } else {
        toast.error('Só é possível enviar planilhas no formato ".xlsx".')
      }
      event.target.value = ''
    }
  }

  const uploadSetOpen = (open: boolean) => {
    dispatch(setSuccessUpload(open))
  }

  const submitFilter = () => {
    onSubmit()
  }

  useEffect(() => {
    dispatch(getAnalysisQuotas([]))
  }, [])

  // NOTE: Commercial Structure is loaded in Home page, but when user access directly to Extra Volume page, it is not loaded
  useEffect(() => {
    // TODO: Check a better way to handle loading of initial commercial structure when force reload - W.I.P
    if (!officesCommercialStructure.length) {
      dispatch(getCommercialStructure())
    }
  }, [officesCommercialStructure])

  const filterValue = (value: string[]) => {
    if (value.length > 1) {
      return value.filter((v) => v !== '')
    } else if (!value.length) {
      return ['']
    } else {
      return value
    }
  }

  const { watch } = useFormContext()

  const selectedMarkets = watch('Mercado') as Channels[]
  const selectedOffices = watch('EscritorioVenda')
  const selectedRegions = watch('EquipeVenda')
  const selectedZones = watch('RegiaoVenda')

  useEffect(() => {
    dispatch(getAnalysisQuotas([]))
  }, [])

  const offices = useMemo(() => {
    if (selectedMarkets.length) {
      const validOffices = getOfficesByMarkets(selectedMarkets)
      const newSelected = validOffices
        .filter((office) => selectedOffices.includes(office.id))
        .map((office) => office.id)
      setValue('EscritorioVenda', newSelected)
      return validOffices
    }

    return getOffices()
  }, [selectedMarkets, officesCommercialStructure])

  const regionals = useMemo(() => {
    if (selectedOffices.length) {
      const validRegions = getRegionalsByOffices(selectedOffices)
      const newSelected = validRegions
        .filter((region) => selectedRegions.includes(region.id))
        .map((region) => region.id)
      setValue('EquipeVenda', newSelected)
      return validRegions
    }

    const regionalsOffices = offices.map((office) => office.id)
    return getRegionalsByOffices(regionalsOffices)
  }, [selectedOffices, selectedMarkets, regionalsCommercialStructure, offices])

  const zones = useMemo(() => {
    if (selectedRegions.length) {
      const validZones = getZonesByRegionals(selectedRegions)
      const newSelected = validZones
        .filter((zone) => selectedZones.includes(zone.id))
        .map((zone) => zone.id)
      setValue('RegiaoVenda', newSelected)
      return validZones
    }

    return getZonesByRegionals(regionals.map((regional) => regional.id))
  }, [selectedRegions, selectedOffices, selectedMarkets, zonesCommercialStructure])

  return (
    <S.Wrapper>
      <S.Header>
        <S.TitleContainer>
          <Typography design="hav-h5-text">Volume Extra</Typography>
          <Typography design="hav-default-04-text">
            Exibindo lista de solicitações de volume extra.
          </Typography>
        </S.TitleContainer>

        <S.ActionsContainer>
          <S.FileInput
            accept={xlsxFileType}
            onChange={handleFileSelect}
            ref={fileInputRef}
            type="file"
          />
          <SlimButton
            content="Carregar arquivo"
            icon={<Icon icon="upload" width="15" />}
            loading={loadingUpload}
            onClick={handleUploadClick}
            type="button"
            variant="secondary"
          />
          <SlimButton
            content="Baixar"
            icon={<Icon icon="download" width="15" />}
            loading={loadingReport}
            onClick={() => setOpenReportModal(true)}
            type="button"
            variant="secondary"
          />
        </S.ActionsContainer>
      </S.Header>

      <S.Form>
        <Controller
          control={control}
          name="TipoCritica"
          render={({ field }) => (
            <MultiselectMenu
              checkAllAction={() => {
                setValue(
                  'TipoCritica',
                  criticismTypeOptions.map((item) => item.label),
                )
              }}
              clearAction={() => setValue('TipoCritica', [])}
              disabled={disableFilters}
              fullWidth
              label="Status"
              onChange={(value) => field.onChange(filterValue(value))}
              optionsList={criticismTypeOptions}
              selectedOptions={field.value ?? []}
            />
          )}
        />
        <Controller
          control={control}
          name="Materiais"
          render={({ field }) => (
            <InputMenu
              clearAction={() => {
                setValue('Materiais', '') // TODO: check if this is correct
                setValue('Materias', ['']) // TODO: check if this is correct
              }}
              disabled={disableFilters}
              fullWidth
              label="Material"
              onChange={(e) => {
                const value = e.target.value
                const batch = value.split('\n').filter((item) => item !== '')
                setValue('Materias', batch) // TODO: check if this is correct
                field.onChange(value)
              }}
              placeholder="Busque por um ou mais códigos de material"
              value={field.value}
            />
          )}
        />
        <Controller
          control={control}
          name="Mercado"
          render={({ field }) => (
            <MultiselectMenu
              checkAllAction={() => {
                setValue(
                  'Mercado',
                  getMarkets().map((item) => item.id),
                )
              }}
              clearAction={() => setValue('Mercado', [])}
              disabled={disableFilters}
              fullWidth
              label="Mercado"
              onChange={(value) => {
                field.onChange(value)
              }}
              optionsList={getMarkets().map((market) => ({
                label: market.description,
                value: market.id,
              }))}
              selectedOptions={field.value ?? []}
            />
          )}
        />
        <Controller
          control={control}
          name="EscritorioVenda"
          render={({ field }) => (
            <MultiselectMenu
              checkAllAction={() => {
                setValue(
                  'EscritorioVenda',
                  offices.map((office) => office.id),
                )
              }}
              clearAction={() => {
                setValue('EscritorioVenda', [])
              }}
              disabled={disableFilters}
              fullWidth
              label="Escritório"
              onChange={(value) => {
                field.onChange(value)
              }}
              optionsList={offices?.map((office) => ({
                label: `${office.id} - ${office.description}`,
                value: office.id,
              }))}
              selectedOptions={field.value ?? []}
            />
          )}
        />
        <Controller
          control={control}
          name="EquipeVenda"
          render={({ field }) => (
            <MultiselectMenu
              checkAllAction={() => {
                setValue(
                  'EquipeVenda',
                  regionals.map((regional) => regional.id),
                )
              }}
              clearAction={() => {
                setValue('EquipeVenda', [])
              }}
              disabled={disableFilters}
              fullWidth
              label="Regional"
              onChange={(value) => {
                field.onChange(value)
              }}
              optionsList={regionals.map((regional) => ({
                label: `${regional.id} - ${regional.description}`,
                value: regional.id,
              }))}
              selectedOptions={field.value ?? []}
            />
          )}
        />
        <Controller
          control={control}
          name="RegiaoVenda"
          render={({ field }) => (
            <MultiselectMenu
              checkAllAction={() => {
                setValue(
                  'RegiaoVenda',
                  zones.map((zone) => zone.id),
                )
              }}
              clearAction={() => setValue('RegiaoVenda', [''])}
              disabled={disableFilters}
              fullWidth
              hasSearch
              label="Zona"
              onChange={(value) => {
                field.onChange(value)
              }}
              optionsList={zones?.map((zone) => {
                return {
                  label: `${zone.id} - ${zone.description}`,
                  value: zone.id,
                }
              })}
              searchPlaceholder="Buscar"
              selectedOptions={field.value ?? []}
            />
          )}
        />
        <Controller
          control={control}
          name="MesesCota"
          render={({ field }) => (
            <MultiselectMenu
              checkAllAction={() => {
                setValue(
                  'MesesCota',
                  filterQuotas.map((quota) => quota.dt_ano_mes_cota),
                )
              }}
              clearAction={() => setValue('MesesCota', [''])}
              disabled={!filterQuotas.length || disableFilters}
              fullWidth
              label="Mês cota"
              onChange={(value) => field.onChange(filterValue(value))}
              optionsList={filterQuotas.map((quota) => {
                return {
                  label: quota.ds_ano_mes_cota,
                  value: quota.dt_ano_mes_cota,
                }
              })}
              selectedOptions={field.value}
            />
          )}
        />
        <SlimButton
          content="Buscar"
          disabled={disableFilters}
          onClick={submitFilter}
          size="sm"
          type="submit"
          variant="primary"
        />
        <Tooltip arrow title="Limpar filtros">
          <span>
            <SlimButton
              disabled={disableFilters}
              icon={<Icon icon="clear-all" size="20" />}
              id="filters-clear-button"
              onClick={onClear}
              type="button"
              variant="secondary"
            />
          </span>
        </Tooltip>
      </S.Form>
      <ModalReport open={openReportModal} setOpen={setOpenReportModal} />
      {loadingUpload && <LoadingModal />}
      <Modal
        buttonAction={() => uploadSetOpen(false)}
        buttonText="Fechar"
        modalWidth={450}
        open={successUpload}
        setOpen={uploadSetOpen}
        showClose={false}
        title={
          <Alert
            kind="success"
            message="Assim que o sistema concluir a análise, uma notificação será enviada para o seu email."
            title="Análise dos pedidos iniciada!"
          />
        }
      >
        <S.ModalText>
          Enquanto aguarda por um retorno do Portal, você pode enviar novas análises.
        </S.ModalText>
      </Modal>
    </S.Wrapper>
  )
}
